import { Transition } from "@headlessui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  type ReactNode,
} from "react";

const FLOATING_ALERT_AUTO_DISMISS_MS = 2500;

type FloatingAlertVariant = "success" | "info" | "error";

export interface FloatingAlertOptions {
  content: ReactNode;
  variant?: FloatingAlertVariant;
}

export interface FloatingAlertManagerHandle {
  enqueueAlert: (options: FloatingAlertOptions) => void;
}

export const FloatingAlertManager = forwardRef<FloatingAlertManagerHandle>(
  (_, ref) => {
    const [activeAlert, setActiveAlert] = useState<FloatingAlertOptions | null>(
      null
    );

    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      enqueueAlert: ({ content, variant = "success" }) => {
        setActiveAlert({ content, variant });
        setIsOpen(true);
      },
    }));

    // Automatically clear the active alert after a timeout
    useEffect(() => {
      if (!isOpen) {
        return undefined;
      }

      const timeoutId = window.setTimeout(() => {
        setIsOpen(false);
      }, FLOATING_ALERT_AUTO_DISMISS_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [isOpen, activeAlert]);

    return (
      <FloatingAlertBanner
        isOpen={isOpen}
        variant={activeAlert?.variant ?? "success"}
      >
        {activeAlert?.content ?? null}
      </FloatingAlertBanner>
    );
  }
);

FloatingAlertManager.displayName = "FloatingAlertManager";

interface FloatingAlertBannerProps {
  isOpen: boolean;
  children: ReactNode;
  variant?: FloatingAlertVariant;
}

const FLOATING_ALERT_SURFACE_STYLE = {
  boxShadow:
    "0 0 2px 0 rgba(40, 41, 61, 0.04), 0 4px 8px 0 rgba(96, 97, 112, 0.16)",
  backdropFilter: "blur(2.7px)",
} as const;

const FLOATING_ALERT_BG_BY_VARIANT: Record<FloatingAlertVariant, string> = {
  success: "rgba(39, 169, 37, 0.85)",
  info: "rgba(66, 110, 152, 0.9)",
  error: "rgba(229, 83, 83, 0.9)",
};

const FloatingAlertBanner = ({
  isOpen,
  children,
  variant = "success",
}: FloatingAlertBannerProps) => {
  return (
    <Transition
      as="div"
      show={isOpen}
      enter="transition ease-out duration-200"
      enterFrom="scale-y-0 opacity-0"
      enterTo="scale-y-100 opacity-100"
      leave="transition ease-in duration-150"
      leaveFrom="scale-y-100 opacity-100"
      leaveTo="scale-y-0 opacity-0"
      className="absolute top-0 right-0 left-0 z-10 flex min-h-10 w-full origin-top items-center justify-center px-6 py-2"
      style={{
        ...FLOATING_ALERT_SURFACE_STYLE,
        background: FLOATING_ALERT_BG_BY_VARIANT[variant],
      }}
    >
      <span className="w-full text-center text-sm leading-[140%] font-normal wrap-break-word whitespace-pre-wrap text-white">
        {children}
      </span>
    </Transition>
  );
};
